import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'

import type { IAuditGroupFilters } from '@/modules/audits/stores/useAuditGroupStore/types/IAuditGroupFilters'
import type { IAuditGroupItem } from '@/modules/audits/stores/useAuditGroupStore/types/IAuditGroupItem'
import type { IAuditGroupStoreState } from '@/modules/audits/stores/useAuditGroupStore/types/IAuditGroupStoreState'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'
import type { IQualityControlStandardTreeItem } from '@/modules/audits/types/IQualityControlStandardTreeItem'
import { mapQualityControlTreeToFilterChoices } from '@/modules/audits/types/IQualityControlStandardTreeItem'

export const useAuditGroupStore = defineStore('auditGroup', {
  state: (): IAuditGroupStoreState => ({
    auditsGroup: [],
    currentGroup: {
      name: '',
      group_assignments: [],
    } as IAuditGroupItem,
    editableGroup: null,
    nextCursor: null,
    totalGroupCount: 0,
    itemsPerPage: 25,
    page: 1,
    currentSortString: null,
    treeChoices: {},
    treeSearchChoices: {},
    treeCursors: {},
    treeLoading: {},

    filters: {
      name: [],
      standards: [],
      parents: [],
      managers: [],
      auditors: [],
      search: '',
    } as IAuditGroupFilters,

    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
  }),

  actions: {
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: any = { field: fieldKey }

        if (search) params.search_field = search
        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditGroupFilters

        if (fieldKey !== 'name' && filters.name.length) {
          params.name = filters.name.join(',')
        }

        if (fieldKey !== 'standards' && filters.standards.length) {
          params.standards = filters.standards.join(',')
        }

        if (fieldKey !== 'parents' && filters.parents.length) {
          params.parents = filters.parents.join(',')
        }

        if (fieldKey !== 'managers' && filters.managers.length) {
          params.managers = filters.managers.join(',')
        }

        if (fieldKey !== 'auditors' && filters.auditors.length) {
          params.auditors = filters.auditors.join(',')
        }

        const response = await axios.get(`${BASE_URL}/api/audit/auditors-group/filter-choices/`, { params })
        const { results, next, strategy } = response.data

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          this.searchChoices[fieldKey] = cursor
            ? [...(this.searchChoices[fieldKey] || []), ...results]
            : results
        } else {
          this.filterChoices[fieldKey] = cursor
            ? [...(this.filterChoices[fieldKey] || []), ...results]
            : results
        }

        this.filterCursors[fieldKey] = next
      } catch (e) {
        console.error(`Ошибка загрузки фильтров групп аудитов [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    /**
     * Получение списка групп аудиторов с фильтрацией и пагинацией
     */
    async fetchAuditsGroups(cursor?: string, itemsPerPage?: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const params: Record<string, any> = {}

        if (this.currentSortString) params.sort = this.currentSortString
        if (itemsPerPage) {
          params.page_size = itemsPerPage
          this.itemsPerPage = itemsPerPage
        }
        if (cursor) params.cursor = cursor

        const filters = this.filters as IAuditGroupFilters

        // Маппинг фильтров согласно API
        if (filters.search) params.search = filters.search
        if (filters.name?.length) params.name = filters.name.join(',')
        if (filters.standards?.length) params.standards = filters.standards.join(',')
        if (filters.parents?.length) params.parents = filters.parents.join(',')
        if (filters.managers?.length) params.managers = filters.managers.join(',')
        if (filters.auditors?.length) params.auditors = filters.auditors.join(',')

        const res = await axios.get(onboardingApiUrl('audit', '/audit/auditors-group/'), { params })

        // Маппинг данных: формируем full_name для менеджера и аудиторов
        const results = res.data.results

        this.nextCursor = res.data.next
        this.totalGroupCount = res.data.count

        if (cursor) {
          this.auditsGroup = [...this.auditsGroup, ...results]
        } else {
          this.auditsGroup = results
        }
      } catch (error) {
        console.error(`❌ Ошибка загрузки групп аудитов: ${String(error)}`)
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить список групп') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Получение группы аудиторов по ID
     */
    async fetchAuditGroupById(id: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const res = await axios.get(`${BASE_URL}/api/audit/auditors-group/${id}/`)

        this.currentGroup = res.data

        this.editableGroup = JSON.parse(JSON.stringify(this.currentGroup))
      } catch (error) {
        console.error('❌ Ошибка загрузки группы аудиторов:', error)
        feedbackStore.showToast({ type: 'error', message: getApiError(error, 'Не удалось загрузить группу') })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    setEmptyGroup() {
      this.currentGroup = {
        name: '',
        group_assignments: [],
      } as IAuditGroupItem
    },

    /**
     * Создание новой группы аудиторов
     */
    async createAuditGroup(): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        // Маппинг данных из currentGroup в формат Request Body API
        const payload = {
          name: this.currentGroup.name,
          group_assignments: this.currentGroup.group_assignments.map(a => ({
            employee: { id: a.employee.id },
            points: a.points.map(c => ({ id: c.id })),
          })),
        }

        await axios.post(`${BASE_URL}/api/audit/auditors-group/`, payload)

        feedbackStore.showToast({ type: 'success', message: 'Группа аудиторов успешно создана' })

        // Сброс данных и обновление списка
        this.setEmptyGroup()
        await this.fetchAuditsGroups()

        // Возврат к списку
        await feedbackStore.goToParentRoute()
      } catch (error) {
        const message = getApiError(error, 'Не удалось создать группу')
        logger.error('createAuditGroup_error', {
          store: 'auditGroup',
          function: 'createAuditGroup',
          error: `❌ Ошибка: ${error}`,
          payload: {
            name: this.currentGroup.name,
          },
        })
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновление существующей группы аудиторов
     */
    async updateAuditGroup(id: number): Promise<void> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const payload = {
          name: this.currentGroup.name,
          group_assignments: this.currentGroup.group_assignments.map(a => ({
            ...(a.id && { id: a.id }),
            employee: { id: a.employee.id },
            points: a.points.map(c => ({ id: c.id })),
          })),
        }

        await axios.put(`${BASE_URL}/api/audit/auditors-group/${id}/`, payload)

        feedbackStore.showToast({ type: 'success', message: 'Группа аудиторов обновлена' })

        this.setEmptyGroup()
        await this.fetchAuditsGroups()
        await feedbackStore.goToParentRoute()
      } catch (error) {
        const message = getApiError(error, 'Не удалось обновить группу')
        logger.error('updateAuditGroup_error', {
          store: 'auditGroup',
          function: 'updateAuditGroup',
          error: `❌ Ошибка: ${error}`,
          payload: { id, name: this.currentGroup.name },
        })
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает данные для TreeSelect в зависимости от модуля.
     *
     * Эндпоинты:
     * - 'audit-standard' -> /api/qualitycontrol/standard/
     * - 'audit-criterion' -> /api/qualitycontrol/standard/?depth=point
     *   При count === 1 разворачиваем children единственного стандарта.
     *   При count >= 2 оставляем полную структуру (root = стандарты).
     */
    async fetchTreeChoices(
      fieldKey: string,
      search?: string,
      cursor?: string,
      module?: IUniversalTreeSelectModule,
    ): Promise<void> {
      this.treeLoading[fieldKey] = true

      // Карта соответствия модулей и эндпоинтов
      const urlMap: Record<string, string> = {
        'audit-standard': '/api/qualitycontrol/standard/',
        'audit-criterion': '/api/qualitycontrol/standard/',
      }

      const path = urlMap[module as string] || '/api/qualitycontrol/criterion/'
      const url = `${BASE_URL}${path}`

      const params: any = {
        search: search || undefined,
        cursor: cursor || undefined,
      }

      // Критерии — запрашиваем полное дерево стандартов с глубиной 4
      if (module === 'audit-criterion') {
        params.depth = 'point'
      }

      try {
        const { data } = await axios.get(url, { params })

        let results = data.results as IQualityControlStandardTreeItem[]

        // Если один стандарт — разворачиваем его children как корень дерева
        if (module === 'audit-criterion' && data.count === 1 && results.length === 1) {
          results = results[0].children || []
        }

        const formattedData = mapQualityControlTreeToFilterChoices(results)

        if (cursor) {
          this.treeChoices[fieldKey] = [
            ...(this.treeChoices[fieldKey] || []),
            ...formattedData,
          ]
        } else if (search) {
          this.treeSearchChoices[fieldKey] = formattedData
        } else {
          this.treeChoices[fieldKey] = formattedData
        }

        this.treeCursors[fieldKey] = data.next
      } catch (error) {
        console.error(`[TreeSelect Error] fieldKey: ${fieldKey}`, error)
      } finally {
        this.treeLoading[fieldKey] = false
      }
    },

    clearTreeChoices(fieldKey: string) {
      if (this.treeChoices[fieldKey]) delete this.treeChoices[fieldKey]
      if (this.treeSearchChoices[fieldKey]) delete this.treeSearchChoices[fieldKey]
      if (this.treeCursors[fieldKey]) delete this.treeCursors[fieldKey]
    },

    async resetFilters() {
      this.filters = {
        name: [],
        standards: [],
        parents: [],
        managers: [],
        auditors: [],
        search: '',
      } as IAuditGroupFilters
      this.currentSortString = null
      this.page = 1
      await this.fetchAuditsGroups()
    },

    resetCurrentGroup() {
      this.currentGroup = {
        name: '',
        group_assignments: [],
      } as IAuditGroupItem
    },
  },
})
